import { ServiceContainer } from '@/services/serviceContainer';
import firebase from 'firebase/app';
import { Deferred } from '../../../../core/helpers/deferredPromise';
import { CacheService } from '../../cache/cacheService';
import { IFirestoreService, QueryOptions, Unsubscribe } from './iFirestoreService';
import { Snapshot } from './snapshot';
import { SnapshotRepository } from './snapshotRepository';

export class FirestoreService implements IFirestoreService {
  private readonly snapshotRepository: SnapshotRepository;
  private readonly cacheService: CacheService;

  public constructor(
    snapshotRepository: SnapshotRepository = ServiceContainer.get(SnapshotRepository),
    cacheService: CacheService = ServiceContainer.get(CacheService),
  ) {
    this.snapshotRepository = snapshotRepository;
    this.cacheService = cacheService;
  }

  public async getCollection<T>(collectionId: string, queryOptions?: QueryOptions): Promise<T[]> {
    const deferred = Deferred.promiseOf<T[]>();
    const requestKey = this.getRequestKey(collectionId, undefined, queryOptions);

    const snapshot = this.getSnapshot<T>(collectionId, undefined, queryOptions);

    if (snapshot.data) {
      return deferred.resolve(snapshot.data as T[]);
    }

    this.subscribeToSnapshot<T[]>(requestKey, deferred);

    this.resolveFromCache(requestKey, (data: T[]) => deferred.resolve(data));

    return deferred.promise;
  }

  public async getDocument<T>(collectionId: string, documentId: string): Promise<T> {
    const deferred = Deferred.promiseOf<T>();
    const requestKey = this.getRequestKey(collectionId, documentId);

    const snapshot = this.getSnapshot<T>(collectionId, documentId);

    if (snapshot.data) {
      return deferred.resolve(snapshot.data as T);
    }

    this.subscribeToSnapshot<T>(requestKey, deferred);

    this.resolveFromCache(requestKey, (data: T) => deferred.resolve(data));

    return deferred.promise;
  }

  private getSnapshot<T>(collection: string, documentId?: string, queryOptions?: QueryOptions): Snapshot {
    const requestKey = this.getRequestKey(collection, documentId, queryOptions);

    if (!this.snapshotRepository.hasSnapshot(requestKey)) {
      if (documentId) {
        this.createDocumentSnapshot<T>(collection, documentId, requestKey);
      } else {
        this.createCollectionSnapshot<T>(collection, requestKey, queryOptions);
      }
    }

    return this.snapshotRepository.getSnapshot(requestKey)!;
  }

  private subscribeToSnapshot<T>(requestKey: string, deferred: Deferred<T>): void {
    const subscriptionId = this.snapshotRepository.addObserver(requestKey, (data) => {
      deferred.resolve(data as T);
      this.snapshotRepository.removeObserver(requestKey, subscriptionId);
    });
  }

  private resolveFromCache<T>(requestKey: string, onLoaded: (data: T) => void): void {
    this.cacheService.get<T>(requestKey).then((cachedData) => {
      if (cachedData) {
        onLoaded(cachedData);
      }
    });
  }

  public subscribeToDocument<T>(collectionId: string, documentId: string, onUpdate: (data: T) => void): Unsubscribe {
    const requestKey = this.getRequestKey(collectionId, documentId);

    const snapshot = this.getSnapshot<T>(collectionId, documentId);

    const observerId = this.snapshotRepository.addObserver(requestKey, onUpdate);

    if (snapshot.data) {
      onUpdate(snapshot.data as T);
    } else {
      this.resolveFromCache(requestKey, onUpdate);
    }

    return () => this.snapshotRepository.removeObserver(requestKey, observerId);
  }

  public subscribeWithoutCache<T>(collectionId: string, onUpdate: (data: T[]) => void, queryOptions?: QueryOptions): Unsubscribe {
    const requestKey = this.getRequestKey(collectionId, undefined);

    const query = this.buildQuery(collectionId, queryOptions);

    const unsubscribe = query.onSnapshot({
      next: (querySnapshot) => {
        const data = this.getSnapshotData<T>(querySnapshot);
        onUpdate(data);
      },
      error: (error) => {
        console.error(`Firestore error for ${requestKey}:`, error);
        throw error;
      },
    });

    return () => unsubscribe();
  }

  public subscribeToCollection<T>(collectionId: string, onUpdate: (data: T[]) => void, queryOptions?: QueryOptions): Unsubscribe {
    const requestKey = this.getRequestKey(collectionId, undefined, queryOptions);

    const snapshot = this.getSnapshot<T>(collectionId, undefined, queryOptions);

    const observerId = this.snapshotRepository.addObserver(requestKey, onUpdate);

    if (snapshot.data) {
      onUpdate(snapshot.data as T[]);
    } else {
      this.resolveFromCache(requestKey, onUpdate);
    }

    return () => this.snapshotRepository.removeObserver(requestKey, observerId);
  }

  public async deleteDocument(collectionId: string, documentId: string): Promise<void> {
    await firebase.firestore().collection(collectionId).doc(documentId).delete();
  }

  private createDocumentSnapshot<T>(collectionId: string, documentId: string, requestKey: string): void {
    const unsubscribe = firebase
      .firestore()
      .collection(collectionId)
      .doc(documentId)
      .onSnapshot({
        next: (docSnapshot) => this.onNextDocumentSnapshot<T>(docSnapshot, requestKey),
        error: (error) => this.onSnapshotError(error, requestKey),
      });
    this.snapshotRepository.registerSnapshot(requestKey, unsubscribe);
  }

  private createCollectionSnapshot<T>(collectionId: string, requestKey: string, queryOptions?: QueryOptions): void {
    const query = this.buildQuery(collectionId, queryOptions);

    const unsubscribe = query.onSnapshot({
      next: (querySnapshot) => this.onNextCollectionSnapshot<T>(querySnapshot, requestKey),
      error: (error) => this.onSnapshotError(error, requestKey),
    });

    this.snapshotRepository.registerSnapshot(requestKey, unsubscribe);
  }

  private onNextCollectionSnapshot = <T>(querySnapshot: firebase.firestore.QuerySnapshot, requestKey: string) => {
    const data = this.getSnapshotData<T>(querySnapshot);

    this.snapshotRepository.publish(requestKey, data);
    this.cacheService.set(requestKey, data);
  };

  private onNextDocumentSnapshot = <T>(docSnapshot: firebase.firestore.DocumentSnapshot, requestKey: string) => {
    const data = this.getDocumentData<T>(docSnapshot);

    this.snapshotRepository.publish(requestKey, data);
    this.cacheService.set(requestKey, data);
  };

  private buildQuery(collectionId: string, queryOptions?: QueryOptions): firebase.firestore.Query {
    let query: firebase.firestore.Query = firebase.firestore().collection(collectionId);

    if (queryOptions) {
      if (queryOptions.where) {
        for (const [field, operator, value] of queryOptions.where) {
          query = query.where(field, operator, value);
        }
      }
      if (queryOptions.orderBy) {
        for (const [field, direction] of queryOptions.orderBy) {
          query = query.orderBy(field, direction);
        }
      }
      if (queryOptions.limit) {
        query = query.limit(queryOptions.limit);
      }
      if (queryOptions.startAfter) {
        query = query.startAfter(queryOptions.startAfter);
      }
      if (queryOptions.endBefore) {
        query = query.endBefore(queryOptions.endBefore);
      }
    }

    return query;
  }

  private getSnapshotData<T>(querySnapshot: firebase.firestore.QuerySnapshot): T[] {
    return querySnapshot.docs.map((doc) => ({
      ...doc.data(),
      id: doc.id,
    })) as T[];
  }

  private getDocumentData<T>(docSnapshot: firebase.firestore.DocumentSnapshot): T {
    return {
      ...docSnapshot.data(),
      id: docSnapshot.id,
    } as T;
  }

  private onSnapshotError = (error: firebase.FirebaseError, requestKey: string) => {
    if (error.code === 'permission-denied') {
      console.info(`Snapshot ${requestKey} closed due to change in auth state.`);
    } else {
      console.error(`Firestore error for ${requestKey}:`, error);
    }
    this.snapshotRepository.removeSnapshot(requestKey);
  };

  private getRequestKey(collectionId: string, documentId?: string, queryOptions?: QueryOptions): string {
    let key = documentId ? `${collectionId}/${documentId}` : collectionId;

    if (queryOptions) {
      const queryString = JSON.stringify(queryOptions, Object.keys(queryOptions).sort());
      key += `?${queryString}`;
    }

    return key;
  }
}
